/* eslint-disable no-redeclare, @typescript-eslint/no-redeclare */
import { ActionIcon, Badge, Divider, Group, Paper, Skeleton, Stack, Text } from '@mantine/core';
import { IconDownload, IconEdit } from '@tabler/icons';
import { Fragment, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import BookitupTable from '../../../Atoms/BookitupTable';
import PanePaper from '../../../Molecules/PanePaper';
import { WindowContext } from '../../../Service/Context/WindowContext';
import { RoutingS } from '../../../Service/RoutingS';
import SubscriptionS from '../../../Service/restapi/subscriptionService';
import { InvoiceListItemDto, InvoiceStatus, CustomerBillingDetailsDto } from '../../../Types/PaymentT';
import { ChargebeeS } from '../../../Service/ChargebeeS';

type InvoiceRow = {
  id: string;
  number: string;
  date: string;
  dateTs: number | null;
  year: string;
  status: InvoiceStatus;
  amount: string;
  issuedCreditNotes: CreditNoteRow[];
};

type CreditNoteRow = {
  id: string;
  date: string;
  status: string;
  amount: string;
  reasonCode: string;
  statusColor: string;
};

const statusConfig: Record<InvoiceStatus, { label: string; color: string }> = {
  PAID: { label: 'Bezahlt', color: 'green' },
  POSTED: { label: 'Veröffentlicht', color: 'blue' },
  PAYMENT_DUE: { label: 'Zahlung fällig', color: 'yellow' },
  NOT_PAID: { label: 'Nicht bezahlt', color: 'red' },
  VOIDED: { label: 'Storniert', color: 'gray' },
  PENDING: { label: 'Ausstehend', color: 'violet' },
  _UNKNOWN: { label: 'Unbekannt', color: 'gray' },
};

const parseInvoiceStatus = (status: string | null | undefined): InvoiceStatus => {
  if (!status) return '_UNKNOWN';
  const normalized = status.toUpperCase() as InvoiceStatus;
  return statusConfig[normalized] ? normalized : '_UNKNOWN';
};

const canDownloadInvoicePdf = (status: InvoiceStatus) => status !== 'PENDING';

const formatTimestamp = (timestamp: number | null) => {
  if (!timestamp) return { date: '-', dateTs: null as number | null };
  const dateTs = timestamp < 10_000_000_000 ? timestamp * 1000 : timestamp;
  return { date: new Date(dateTs).toLocaleDateString('de-DE'), dateTs };
};

const prettifyCode = (value: string | null | undefined) => {
  if (!value) return '-';
  return value
    .toLowerCase()
    .split('_')
    .map((part) => (part ? `${part[0].toUpperCase()}${part.slice(1)}` : ''))
    .join(' ');
};

const getCreditNoteStatusUI = (status: string | null | undefined) => {
  const normalized = (status || '_UNKNOWN').toUpperCase();
  if (normalized.includes('REFUND') || normalized.includes('APPLIED')) {
    return { status: 'ERSTATTET', statusColor: 'green' };
  }
  if (normalized.includes('PENDING')) {
    return { status: 'Ausstehend', statusColor: 'yellow' };
  }
  if (normalized.includes('VOID')) {
    return { status: 'Storniert', statusColor: 'gray' };
  }
  return { status: 'Unbekannt', statusColor: 'blue' };
};

const InvoicesSection = () => {
  const [loading, setLoading] = useState(true);
  const { xs, sm } = useContext(WindowContext);
  const [invoices, setInvoices] = useState<InvoiceListItemDto[]>([]);
  const [downloadLoadingId, setDownloadLoadingId] = useState<string | null>(null);
  const [billingDetails, setBillingDetails] = useState<CustomerBillingDetailsDto | null>(null);

  useEffect(() => {
    RoutingS.changeTitle('Rechnungen');
  }, []);

  useEffect(() => {
    SubscriptionS.getChargebeeInvoices()
      .then((res) => {
        if (Array.isArray(res)) {
          setInvoices(res as InvoiceListItemDto[]);
          return;
        }

        setInvoices([]);
      })
      .finally(() => setLoading(false));
  }, []);

  const refreshBillingDetails = useCallback(() => {
    SubscriptionS.getChargebeeBillingDetails()
      .then((res) => {
        if (res) {
          setBillingDetails(res as CustomerBillingDetailsDto);
        }
      })
      .catch(() => {
        setBillingDetails(null);
      });
  }, []);

  useEffect(() => {
    refreshBillingDetails();
  }, [refreshBillingDetails]);

  const editDetails = (section: string) => ChargebeeS.openPortal(refreshBillingDetails, section);

  const formattedInvoices = useMemo<InvoiceRow[]>(
    () =>
      invoices.map((invoice) => {
        const { date, dateTs } = formatTimestamp(invoice.date);
        const year = dateTs ? String(new Date(dateTs).getFullYear()) : 'Ohne Datum';
        const status = parseInvoiceStatus(invoice.status);
        const issuedCreditNotes = (invoice.issuedCreditNotes || []).map((creditNote) => {
          const statusUI = getCreditNoteStatusUI(creditNote.status);
          const creditNoteAmount =
            creditNote.amount || creditNote.amount === 0
              ? new Intl.NumberFormat('de-DE', {
                  style: 'currency',
                  currency: invoice.currency || 'EUR',
                }).format(creditNote.amount / 100)
              : '-';

          return {
            id: creditNote.id,
            date: formatTimestamp(creditNote.date).date,
            status: statusUI.status,
            amount: creditNoteAmount,
            reasonCode: prettifyCode(creditNote.reasonCode),
            statusColor: statusUI.statusColor,
          };
        });

        const amount =
          invoice.amount || invoice.amount === 0
            ? new Intl.NumberFormat('de-DE', {
                style: 'currency',
                currency: invoice.currency || 'EUR',
              }).format(invoice.amount / 100)
            : '-';

        return {
          id: invoice.id,
          number: invoice.number || invoice.id,
          date,
          dateTs,
          year,
          status,
          amount,
          issuedCreditNotes,
        };
      }),
    [invoices],
  );

  const groupedInvoices = useMemo(() => {
    const sorted = [...formattedInvoices].sort((a, b) => {
      if (a.dateTs === null && b.dateTs === null) return 0;
      if (a.dateTs === null) return 1;
      if (b.dateTs === null) return -1;
      return b.dateTs - a.dateTs;
    });

    return sorted.reduce<Record<string, InvoiceRow[]>>((acc, invoice) => {
      const key = invoice.year;
      return { ...acc, [key]: [...(acc[key] || []), invoice] };
    }, {});
  }, [formattedInvoices]);

  const { length } = formattedInvoices;

  const getBackendDocumentUrl = (id: string, type: 'invoice' | 'creditNote') => {
    const suffix = type === 'creditNote' ? 'credit-notes' : 'invoices';
    return `${process.env.REACT_APP_PAYMENT_SERVICE_URL}/chargebee/${suffix}/${id}`;
  };

  const downloadDocument = async (id: string, type: 'invoice' | 'creditNote' = 'invoice') => {
    setDownloadLoadingId(id);

    const url = getBackendDocumentUrl(id, type);

    try {
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (error) {
      RoutingS.openInNewTab(url);
    } finally {
      setDownloadLoadingId(null);
    }
  };

  const mapCreditNoteUI = (creditNote: CreditNoteRow, invoiceNumber: string) => {
    const { id, date, status, amount, reasonCode, statusColor } = creditNote;
    const displayId = id.length > 12 ? `${id.slice(0, 12)}...` : id;

    return (
      <tr key={`${invoiceNumber}-${id}`} style={{ color: 'dimmed' }}>
        <td style={{ paddingLeft: 26 }}>
          <Text size="sm">#{displayId}</Text>
        </td>
        {!xs && <td>{date}</td>}
        <td>
          <Badge radius="sm" variant="filled" color={statusColor}>
            {status}
          </Badge>
        </td>
        <td>
          <Text size="sm" weight={500}>
            {amount}
          </Text>
          <Text size="xs" color="dimmed" lineClamp={1}>
            {reasonCode}
          </Text>
        </td>
        <td>
          <Group spacing="xs" position="right">
            <ActionIcon size="xs" onClick={() => downloadDocument(id, 'creditNote')} loading={downloadLoadingId === id}>
              <IconDownload />
            </ActionIcon>
          </Group>
        </td>
      </tr>
    );
  };

  const mapInvoiceUI = (invoice: InvoiceRow) => {
    const { id, number, date, status, amount, issuedCreditNotes } = invoice;
    const cfg = statusConfig[status];
    const downloadable = canDownloadInvoicePdf(status);
    const hasCreditNotes = issuedCreditNotes.length > 0;

    return (
      <Fragment key={id}>
        <tr>
          <td>{number}</td>
          {!xs && <td>{date}</td>}
          <td>
            <Badge radius="sm" variant="filled" color={cfg.color}>
              {cfg.label}
            </Badge>
          </td>
          <td>{amount}</td>
          <td>
            <Group spacing="xs" position="right">
              <ActionIcon
                size="xs"
                onClick={() => downloadDocument(id)}
                loading={downloadLoadingId === id}
                disabled={!downloadable}
                title={!downloadable ? 'PDF ist für diesen Status nicht verfügbar.' : 'PDF herunterladen'}
              >
                <IconDownload />
              </ActionIcon>
            </Group>
          </td>
        </tr>
        {hasCreditNotes && (
          <tr style={{ backgroundColor: 'transparent' }}>
            <td colSpan={5} style={{ paddingBottom: 2, paddingLeft: 26 }}>
              <Text size="xs" weight={700} color="dimmed">
                Zugehörige Gutschriften
              </Text>
            </td>
          </tr>
        )}
        {issuedCreditNotes.length > 0 && issuedCreditNotes.map((creditNote) => mapCreditNoteUI(creditNote, number))}
        {issuedCreditNotes.length > 0 && (
          <tr style={{ backgroundColor: 'transparent' }}>
            <td colSpan={5} style={{ padding: '4px 0 8px 26px' }}>
              <div
                style={{
                  height: 1,
                  marginRight: 8,
                  backgroundColor: '#e9ecef',
                }}
              />
            </td>
          </tr>
        )}
      </Fragment>
    );
  };

  const renderBillingAddressCard = () => (
    <Paper withBorder radius="md" p="md">
      <Stack spacing="xs">
        <Group position="apart" spacing="xs">
          <Group>
            <Text size="sm" weight={600}>
              Rechnungsadresse
            </Text>
          </Group>
          <ActionIcon size="xs">
            <IconEdit onClick={() => editDetails('EDIT_BILLING_ADDRESS')} />
          </ActionIcon>
        </Group>

        <Divider />

        {billingDetails?.billingAddress ? (
          <Stack spacing={2}>
            <Text size="sm" weight={500}>
              {billingDetails.billingAddress.firstName || ''} {billingDetails.billingAddress.lastName || ''}
            </Text>

            <Text size="sm" color="dimmed">
              {billingDetails.billingAddress.line1}
            </Text>

            {billingDetails.billingAddress.line2 && (
              <Text size="sm" color="dimmed">
                {billingDetails.billingAddress.line2}
              </Text>
            )}

            <Text size="sm" color="dimmed">
              {billingDetails.billingAddress.zip} {billingDetails.billingAddress.city}
            </Text>

            <Text size="sm" color="dimmed">
              {billingDetails.billingAddress.country}
            </Text>
          </Stack>
        ) : (
          <Text size="sm" color="dimmed">
            Keine Rechnungsadresse hinterlegt.
          </Text>
        )}
      </Stack>
    </Paper>
  );

  const renderPaymentMethodCard = () => {
    const paymentMethod = billingDetails?.paymentMethod;
    const hasCardDetails = Boolean(paymentMethod?.cardBrand || paymentMethod?.cardMaskedNumber);
    const hasBankDetails = Boolean(paymentMethod?.bankName || paymentMethod?.bankAccountLast4);

    return (
      <Paper withBorder radius="md" p="md">
        <Stack spacing="xs">
          <Group position="apart">
            <Group spacing="xs">
              <Text size="sm" weight={600}>
                Zahlungsmethode
              </Text>
            </Group>
            <ActionIcon size="xs">
              <IconEdit onClick={() => editDetails('PAYMENT_SOURCES')} />
            </ActionIcon>
          </Group>

          <Divider />

          {paymentMethod ? (
            <Stack spacing={2}>
              <Text size="sm" weight={500}>
                {prettifyCode(paymentMethod.type)}
              </Text>

              {paymentMethod.cardBrand && (
                <Text size="sm" color="dimmed">
                  {paymentMethod.cardBrand}
                </Text>
              )}

              {paymentMethod.cardMaskedNumber && (
                <Text size="sm" color="dimmed">
                  {paymentMethod.cardMaskedNumber}
                </Text>
              )}

              {paymentMethod.bankName && (
                <Text size="sm" color="dimmed">
                  {paymentMethod.bankName}
                </Text>
              )}

              {paymentMethod.bankAccountLast4 && (
                <Text size="sm" color="dimmed">
                  Konto endet auf {paymentMethod.bankAccountLast4}
                </Text>
              )}

              {!hasCardDetails && !hasBankDetails && (
                <Text size="sm" color="dimmed">
                  {paymentMethod.gateway}
                </Text>
              )}
            </Stack>
          ) : (
            <Text size="sm" color="dimmed">
              Keine Zahlungsmethode hinterlegt.
            </Text>
          )}
        </Stack>
      </Paper>
    );
  };

  return (
    <Stack mb={70} spacing="lg">
      <PanePaper borderlessVersion withoutBorder>
        <Stack spacing="md">
          {!billingDetails && <Skeleton height={80} radius="md" />}

          {billingDetails &&
            (xs || sm ? (
              <Stack spacing="md">
                {renderBillingAddressCard()}
                {renderPaymentMethodCard()}
              </Stack>
            ) : (
              <Group align="stretch" spacing="xl" grow>
                {renderBillingAddressCard()}
                {renderPaymentMethodCard()}
              </Group>
            ))}
        </Stack>
      </PanePaper>

      <PanePaper borderlessVersion title={xs ? 'Rechnungen' : undefined} withoutBorder>
        {loading && <Skeleton height={60} width="100%" />}
        {!loading && length === 0 && (
          <Stack spacing={6} py="sm">
            <Text italic color="dimmed" size="sm">
              Es sind noch keine Rechnungen vorhanden.
            </Text>
            <Text color="dimmed" size="xs">
              Rechnungen erscheinen hier automatisch, sobald eine Abrechnung erstellt wurde.
            </Text>
          </Stack>
        )}
        {!loading && length > 0 && (
          <BookitupTable>
            <thead>
              <tr>
                <th>Rechnungsnummer</th>
                {!xs && <th>Datum</th>}
                <th>Status</th>
                <th>{xs ? 'Info' : 'Betrag'}</th>
                <th> </th>
              </tr>
            </thead>
            <tbody>
              {Object.keys(groupedInvoices)
                .sort((a, b) => Number(b) - Number(a))
                .map((year) => {
                  const yearInvoices = groupedInvoices[year];
                  return (
                    <Fragment key={year}>
                      <tr style={{ backgroundColor: 'transparent' }}>
                        <td colSpan={5}>
                          <Text weight="bold" size="xl" my="md" ml="md">
                            {year}
                          </Text>
                        </td>
                      </tr>
                      {yearInvoices.map(mapInvoiceUI)}
                    </Fragment>
                  );
                })}
            </tbody>
          </BookitupTable>
        )}
      </PanePaper>
    </Stack>
  );
};

export default InvoicesSection;
